import React, { useState } from 'react';
import { useTheme, Typography } from '@mui/material';
import MultiStepModal, { ModalStepT } from 'ui/Modals/MultiStepModal';
import { useTranslation } from 'react-i18next';
import Flex from 'ui/Flex/Flex';
import CreateUsersList from './CreateUsersList';
import CreateMultipleUserProvider from './context/CreateMultipleUserProvider';
import { MultipleUsersRolesAccessSection } from '../CreateOrEditUser/components/UserFormGeneralSection/components/RolesAccessSection';
import UserFormActivationSection from '../CreateOrEditUser/components/UserFormActivationSection';
import {
    useCreateMultipleUserContext,
    useCreateMultipleUserDispatchContext,
} from './context/CreateMultipleUserContext';
import { CreateMultipleUserActionType } from './context/types';
import { useCreateUsers } from '../CreateOrEditUser/hooks/useCreateUser';

type Props = {
    setIsModalOpen: (a: boolean) => void;
    existingUsersEmails: string[];
};

const ACTIVATION_SECTION_TRANSLATION_PATH =
    'adminSpace.createUsers.modalStepper.activationSection.emailScheduling';

const CreateMultipleUserModal = ({
    setIsModalOpen,
    existingUsersEmails,
}: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [nextStepEnable, setNextStepEnable] = useState<boolean>(true);

    const { users, shouldSendEmail, isDirty } = useCreateMultipleUserContext();
    const dispatch = useCreateMultipleUserDispatchContext();
    const { mutate } = useCreateUsers(users, shouldSendEmail);

    const onSubmit = () => {
        mutate();
        setIsModalOpen(false);
    };

    const closeModal = () => {
        if (!isDirty) {
            setIsModalOpen(false);
        } else {
            const close = !window.confirm(
                t('adminSpace.createUser.leavingModalConfirmation'),
            );
            setIsModalOpen(close);
        }
    };

    const steps: ModalStepT[] = [
        {
            node: (
                <CreateUsersList
                    existingUsersEmails={existingUsersEmails}
                    setFormIsValid={(valid: boolean) =>
                        setNextStepEnable(valid)
                    }
                />
            ),
            title: (
                <Flex gap={2} flexDirection="column">
                    <Typography variant="h4">
                        {t(
                            'adminSpace.createUsers.modalStepper.listSection.title',
                        )}
                    </Typography>
                    <Typography>
                        {t(
                            'adminSpace.createUsers.modalStepper.listSection.subtitle',
                        )}
                    </Typography>
                </Flex>
            ),
            confirmAction: {
                enabled: nextStepEnable,
            },
        },
        {
            node: (
                <MultipleUsersRolesAccessSection
                    setNextStepEnable={setNextStepEnable}
                />
            ),
            title: (
                <Flex gap={2} flexDirection="column">
                    <Typography variant="h4">
                        {t(
                            'adminSpace.createUsers.modalStepper.rolesSection.title',
                        )}
                    </Typography>
                    <Typography>
                        {t(
                            'adminSpace.createUsers.modalStepper.rolesSection.subtitle',
                            {
                                nbUser: users.length,
                            },
                        )}
                    </Typography>
                </Flex>
            ),
            confirmAction: {
                enabled: nextStepEnable,
            },
        },
        {
            node: (
                <UserFormActivationSection
                    setShouldSendActivationEmail={(should: boolean) => {
                        dispatch({
                            type: CreateMultipleUserActionType.setSendEmail,
                            shouldSendEmail: should,
                        });
                    }}
                    shouldSendActivationEmail={shouldSendEmail}
                    translationPath={ACTIVATION_SECTION_TRANSLATION_PATH}
                />
            ),
            title: (
                <Typography variant="h4">
                    {t(
                        'adminSpace.createUsers.modalStepper.activationSection.title',
                    )}
                </Typography>
            ),
            confirmAction: {
                label: 'common.complete',
                onClick: onSubmit,
                shouldTriggerDefaultOnClose: false,
                className: 'pendo-create-multiple-users-confirm',
            },
        },
    ];

    return (
        <MultiStepModal
            maxWidth="lg"
            steps={steps}
            PaperProps={{
                sx: { backgroundColor: theme.palette.lightGrey },
            }}
            onClose={closeModal}
        />
    );
};

const CreateMultipleUserModalWrapper = (props: Props) => (
    <CreateMultipleUserProvider>
        <CreateMultipleUserModal {...props} />
    </CreateMultipleUserProvider>
);

export default CreateMultipleUserModalWrapper;
